import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Tooltip } from '@chakra-ui/react'

export type ResponsiveTooltipProps = {
  initialLabel: string
  successLabel?: string
  failureLabel?: string
  textToCopy?: string
  getCopyText?: () => Promise<string> | string
  placement?:
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end'
  hasArrow?: boolean
  isDisabled?: boolean
  onClick?: (event: React.MouseEvent<HTMLElement>, isSuccess: boolean) => void
  // Content to render inside the tooltip (typically an icon button or icon)
  children: React.ReactNode
}

/**
 * ResponsiveTooltip
 * A small helper that shows an initial label, runs a copy/action on click,
 * then changes the tooltip label to a success message for a short duration,
 * and reverts automatically.
 */
const ResponsiveTooltip = ({
  initialLabel = 'Copy',
  successLabel,
  failureLabel,
  textToCopy,
  getCopyText,
  children,
  onClick,
  placement = 'top',
  hasArrow = false,
}: ResponsiveTooltipProps): React.ReactElement => {
  const [label, setLabel] = useState<string>(initialLabel)

  const handleClick: React.MouseEventHandler<HTMLElement> = useCallback(
    async e => {
      e.stopPropagation()
      let isSuccess = false
      try {
        const copyText = getCopyText ? await getCopyText() : textToCopy
        if (navigator.clipboard && copyText) {
          await navigator.clipboard.writeText(copyText)
          if (successLabel) {
            setLabel(successLabel)
          }
          isSuccess = true
        }
      } catch (err) {
        if (failureLabel) {
          setLabel(failureLabel)
        }
        isSuccess = false
      } finally {
        if (onClick) {
          onClick(e, isSuccess)
        }
      }
    },
    [textToCopy, getCopyText, successLabel, failureLabel, onClick],
  )

  return (
    <Tooltip
      label={label}
      placement={placement}
      hasArrow={hasArrow}
      shouldWrapChildren
      arrowSize={8}
      trigger="hover"
      onClose={() => setLabel(initialLabel)}
    >
      <span
        tabIndex={-1} // not in tab order
        onMouseDown={e => e.preventDefault()} // block focus on mouse click
        onClick={handleClick}
        role="presentation"
      >
        {children}
      </span>
    </Tooltip>
  )
}

export default ResponsiveTooltip
